import { useMutation } from "@apollo/client/react";
import {
  ContentBreadcrumbFragment_context,
  CreateMultipleChoiceAnswer,
  CreateMultipleChoiceAnswerVariables,
  MultipleChoiceData,
} from "__generated__/types";
import { Button } from "common/buttons";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { useFinishContent } from "domain/common/content-mutations";
import gql from "graphql-tag";
import React, { useState } from "react";
import { FormQuestions } from "./MultipleChoice";
import { MultipleChoiceSummaryQuestion } from "./MultipleChoiceSummaryQuestion";
import { Result } from "./Result";

interface Props {
  questions: MultipleChoiceData["questions"];
  formQuestions: FormQuestions;
  multipleChoiceId: string;
  goToQuestion: (idx: number) => void;
  resetForm: () => void;
  containerType:
    | ContentBreadcrumbFragment_context["__typename"]
    | "SelfAssessment"
    | undefined;
  onFinishedElement?: () => void;
  isSyllabus?: boolean;
}

const MUTATION = gql`
  mutation CreateMultipleChoiceAnswer(
    $multiplechoiceId: ID!
    $selectedOptions: [ID!]!
  ) {
    createMultipleChoiceAnswer(
      input: {
        multipleChoiceId: $multiplechoiceId
        selectedOptions: $selectedOptions
      }
    ) {
      multipleChoiceAnswer {
        id
        scoreInPercent
      }
    }
  }
`;

function extractOptionIds(data: FormQuestions) {
  const output = [];
  for (const options of Object.values(data)) {
    if (options !== undefined) {
      for (const option of options.selectedOptions) {
        output.push(option);
      }
    }
  }

  return output;
}

function test(
  formQuestions: FormQuestions,
  questions: MultipleChoiceData["questions"],
) {
  // TODO: This is not correct as items will be present
  //  when removing a checkbox so it cannot be a length check.
  return Object.keys(questions).length === Object.keys(formQuestions).length;
}

export const MultipleChoiceSummary: React.FC<
  React.PropsWithChildren<Props>
> = ({
  formQuestions,
  questions,
  multipleChoiceId,
  goToQuestion,
  resetForm,
  onFinishedElement,
  isSyllabus,
  containerType,
}) => {
  const [delivered, setDelivered] = useState(false);
  const { handleFinishContent } = useFinishContent();
  const isSelfAssessment = containerType === "SelfAssessment";

  const [mutate, { data, error, loading }] = useMutation<
    CreateMultipleChoiceAnswer,
    CreateMultipleChoiceAnswerVariables
  >(MUTATION);

  const onSubmit = async () => {
    const result = await mutate({
      variables: {
        multiplechoiceId: multipleChoiceId,
        selectedOptions: extractOptionIds(formQuestions),
      },
    });

    if (result.data) {
      setDelivered(true);

      if (isSyllabus && !isSelfAssessment) {
        await handleFinishContent(multipleChoiceId);

        if (onFinishedElement) {
          onFinishedElement();
        }
      }
    }
  };

  const canSubmit = test(formQuestions, questions);

  const score =
    data?.createMultipleChoiceAnswer.multipleChoiceAnswer.scoreInPercent;

  return (
    <div>
      {delivered && typeof score === "number" ? (
        <Result score={score} resetForm={resetForm} />
      ) : (
        <div>
          <p>
            {canSubmit
              ? "Vil du se over svarene dine før du lagrer? Trykk på spørsmålet under."
              : "Du har ikke svart på alle spørsmålene enda! Trykk på spørsmålene under for å svare."}
          </p>
          {questions.map((question, index) => (
            <MultipleChoiceSummaryQuestion
              key={question.id}
              question={question}
              value={formQuestions[question.id]}
              index={index}
              goToQuestion={goToQuestion}
            />
          ))}

          <MutationLoadingOrError loading={false} error={error} />
          <Button
            loading={loading}
            onClick={onSubmit}
            kind={canSubmit ? "primary" : "disabled"}
          >
            {!isSelfAssessment ? "Vis resultatet mitt" : "Lagre"}
          </Button>
        </div>
      )}
    </div>
  );
};
